/**
 * 模型顏色控制器 - 純材質修改版本
 * 
 * 策略：
 * - 直接修改 THREE.Material 的顏色和透明度
 * - 無限次修改，無任何限制
 * - 適合按類別（category）或按模型著色
 * 
 * 注意：
 * - 無法精確控制單個 localId
 * - 會影響共享同一材質的所有元素
 */

import * as THREE from "three";
import { MultiModelManager } from "./multiModelManager";

export interface ColorOptions {
  color: string | THREE.Color;
  opacity?: number;
}

interface MaterialBackup {
  originalColor: THREE.Color;
  originalOpacity: number;
  originalTransparent: boolean;
}

export class ModelColorController {
  private modelManager: MultiModelManager;
  private map: any;
  
  // 記錄修改過的材質及其原始狀態
  private modifiedMaterials: Map<string, Map<THREE.Material, MaterialBackup>> = new Map();

  constructor(modelManager: MultiModelManager, map?: any) {
    this.modelManager = modelManager;
    this.map = map;
  }

  /**
   * 為整個類別設置顏色
   * 這是主要的著色方法
   */
  async setCategoryColor(
    modelId: string,
    categoryPattern: string | RegExp,
    options: ColorOptions
  ): Promise<void> {
    const model = this.modelManager.getModel(modelId);
    
    if (!model) {
      console.error(`❌ Model not found: ${modelId}`);
      return;
    }

    const modelInfo = this.modelManager.getAllModels().get(modelId);
    if (!modelInfo || modelInfo.modelType === 'gltf') return; // GLTF 不支援
    
    try {
      const pattern = typeof categoryPattern === "string" 
        ? new RegExp(`^${categoryPattern}$`) 
        : categoryPattern;
      
      // 獲取類別的所有元素 ID
      const categoryIds = await (model as any).getItemsOfCategories([pattern]);
      
      let totalIds: number[] = [];
      for (const ids of Object.values(categoryIds)) {
        totalIds = totalIds.concat(ids as any);
      }

      if (totalIds.length === 0) {
        console.warn(`⚠️ No elements found for category: ${categoryPattern}`);
        return;
      }
      
      // 修改這些元素的材質
      await this.setElementsColorByMaterial(modelId, totalIds, options);
    } catch (error) {
      console.error(`❌ Error coloring category:`, error);
      throw error;
    }
  }

  /**
   * 通過修改材質來設置元素顏色
   */
  private async setElementsColorByMaterial(
    modelId: string,
    _localIds: number[],
    options: ColorOptions
  ): Promise<void> {
    const model = this.modelManager.getModel(modelId);
    if (!model) return;

    const color = typeof options.color === "string" 
      ? new THREE.Color(options.color) 
      : options.color;
    
    const opacity = options.opacity ?? 1;
    
    // 獲取或創建材質備份 Map
    if (!this.modifiedMaterials.has(modelId)) {
      this.modifiedMaterials.set(modelId, new Map());
    }
    const backupMap = this.modifiedMaterials.get(modelId)!;
    
    const modelInfo = this.modelManager.getAllModels().get(modelId);
    if (!modelInfo || modelInfo.modelType === 'gltf') return; // GLTF 不支援
    
    let modifiedCount = 0;
    
    // 遍歷模型，找出對應的 mesh
    (model as any).object.traverse((child: any) => {
      if (!(child as THREE.Mesh).isMesh) return;
      
      const mesh = child as THREE.Mesh;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      
      materials.forEach((mat) => {
        if (!mat) return;
        
        const meshMat = mat as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial;
        
        // 備份原始狀態（如果還沒備份）
        if (!backupMap.has(mat)) {
          backupMap.set(mat, {
            originalColor: meshMat.color.clone(),
            originalOpacity: meshMat.opacity,
            originalTransparent: meshMat.transparent
          });
        }
        
        // 修改材質
        meshMat.color.copy(color);
        meshMat.opacity = opacity;
        meshMat.transparent = opacity < 1;
        meshMat.needsUpdate = true;
        
        modifiedCount++;
      });
    });
    
    // 觸發重繪
    if (this.map) {
      this.map.triggerRepaint();
    }
  }

  /**
   * 空的初始化方法（兼容性）
   */
  initModelVertexColor(_modelId: string): void {
  }

  /**
   * 為指定元素設置顏色（通過材質）
   * 注意：這個方法會影響整個模型的所有材質
   */
  async setElementsColor(
    modelId: string,
    localIds: number[],
    options: ColorOptions
  ): Promise<void> {
    await this.setElementsColorByMaterial(modelId, localIds, options);
  }

  /**
   * 重置顏色（恢復原始材質）
   */
  resetElementsColor(modelId: string, _localIds?: number[]): void {
    const backupMap = this.modifiedMaterials.get(modelId);
    if (!backupMap) return;
    
    let resetCount = 0;
    
    // 恢復所有修改過的材質
    for (const [mat, backup] of backupMap.entries()) {
      const meshMat = mat as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial;
      meshMat.color.copy(backup.originalColor);
      meshMat.opacity = backup.originalOpacity;
      meshMat.transparent = backup.originalTransparent;
      meshMat.needsUpdate = true;
      resetCount++;
    }
    
    backupMap.clear();
    
    // 觸發重繪
    if (this.map) {
      this.map.triggerRepaint();
    }
  }

  /**
   * 重置所有顏色
   */
  resetAll(): void {
    for (const [modelId] of this.modifiedMaterials) {
      this.resetElementsColor(modelId);
    }
    
    this.modifiedMaterials.clear();
    
    if (this.map) {
      this.map.triggerRepaint();
    }
  }

  /**
   * 獲取模型的所有類別
   */
  async getCategories(modelId: string): Promise<string[]> {
    const model = this.modelManager.getModel(modelId);
    if (!model) return [];
    
    const modelInfo = this.modelManager.getAllModels().get(modelId);
    if (!modelInfo || modelInfo.modelType === 'gltf') return []; // GLTF 不支援

    try {
      return await (model as any).getCategories();
    } catch (error) {
      console.error(`❌ Error getting categories:`, error);
      return [];
    }
  }

  /**
   * 獲取指定類別的所有元素 ID
   */
  async getCategoryElements(
    modelId: string,
    categoryPattern: string | RegExp
  ): Promise<number[]> {
    const model = this.modelManager.getModel(modelId);
    if (!model) return [];
    
    const modelInfo = this.modelManager.getAllModels().get(modelId);
    if (!modelInfo || modelInfo.modelType === 'gltf') return []; // GLTF 不支援

    try {
      const pattern = typeof categoryPattern === "string" 
        ? new RegExp(`^${categoryPattern}$`) 
        : categoryPattern;
      
      const categoryIds = await (model as any).getItemsOfCategories([pattern]);
      
      let totalIds: number[] = [];
      for (const ids of Object.values(categoryIds)) {
        totalIds = totalIds.concat(ids as any);
      }

      return totalIds;
    } catch (error) {
      console.error(`❌ Error getting category elements:`, error);
      return [];
    }
  }

  /**
   * 獲取著色統計
   */
  getColoredStats(): Record<string, any> {
    const stats: Record<string, number> = {};
    let totalMaterials = 0;
    
    for (const [modelId, backupMap] of this.modifiedMaterials) {
      stats[modelId] = backupMap.size;
      totalMaterials += backupMap.size;
    }
    
    return {
      modelsColored: this.modifiedMaterials.size,
      materialsByModel: stats,
      totalMaterials,
    };
  }
}
